/**
 * `BannerAdDto` / `BannerAdBindingModel`, from
 * `Gopanza.Api.Services.Abstractions`. The read and write shapes differ: the
 * DTO adds `id`, `createdAt`, `businessName` and the server-computed
 * `isCurrentlyActive`, none of which are sent back.
 */

/** `BannerPlacement`. */
export const PLACEMENT = {
  top: 0,
  bottom: 1,
  left: 2,
  right: 3,
  popup: 4,
} as const;

/** `BannerAction`. `none` means the banner is not tappable. */
export const BANNER_ACTION = { none: 0, openLink: 1, search: 2 } as const;

export interface BannerAd {
  id: string;
  createdAt: string | null;
  name: string | null;
  businessId: string | null;
  /** Resolved server-side, so the list needs no store lookup. */
  businessName: string | null;
  startDate: string | null;
  endDate: string | null;
  approved: boolean;
  ordering: number;
  placement: number;
  /** The English asset. */
  imageUrl: string | null;
  imageUrlSpa: string | null;
  actionType: number;
  actionValue: string | null;
  backgroundColor: string | null;
  showInApp: boolean;
  showInWeb: boolean;
  /**
   * Computed by the DTO: approved, and inside its window. Trusted rather than
   * recomputed — it is what the storefront queries actually filter on, so a
   * second opinion here could disagree with what shoppers see.
   */
  isCurrentlyActive: boolean;
}

/** Exactly what `bannerad/create` and `bannerad/update/{id}` accept. */
export interface BannerAdInput {
  name: string;
  startDate: string | null;
  endDate: string | null;
  placement: number;
  imageUrl: string;
  imageUrlSpa: string;
  approved: boolean;
  actionType: number;
  actionValue: string;
  ordering: number;
  businessId: string | null;
  backgroundColor: string;
  showInApp: boolean;
  showInWeb: boolean;
}

export type BannerStatus = 'live' | 'scheduled' | 'ended' | 'unapproved';

/**
 * Why a banner is or is not showing.
 *
 * The server's `isCurrentlyActive` answers the yes/no and is taken as final —
 * it is the same rule the storefront queries apply, so deriving a second
 * opinion here risks the portal calling a banner live that shoppers cannot see.
 * Only the *reason* for a "no" is worked out locally, and only that reason,
 * which is what the status column has to explain.
 */
export function statusOf(banner: BannerAd, now = Date.now()): BannerStatus {
  if (banner.isCurrentlyActive) return 'live';
  if (!banner.approved) return 'unapproved';

  const end = banner.endDate ? Date.parse(`${banner.endDate}Z`) : null;
  if (end !== null && now > end) return 'ended';

  // Approved, not past its end, and the server still says it is not running:
  // the only reason left is that it has not started yet.
  return 'scheduled';
}

/** The read shape as the write shape. */
export function toInput(banner: BannerAd): BannerAdInput {
  return {
    name: banner.name ?? '',
    startDate: banner.startDate,
    endDate: banner.endDate,
    placement: banner.placement,
    imageUrl: banner.imageUrl ?? '',
    imageUrlSpa: banner.imageUrlSpa ?? '',
    approved: banner.approved,
    actionType: banner.actionType,
    actionValue: banner.actionValue ?? '',
    ordering: banner.ordering,
    businessId: banner.businessId,
    backgroundColor: banner.backgroundColor ?? '',
    showInApp: banner.showInApp,
    showInWeb: banner.showInWeb,
  };
}

export function emptyBanner(): BannerAdInput {
  return {
    name: '',
    startDate: null,
    endDate: null,
    placement: PLACEMENT.top,
    imageUrl: '',
    imageUrlSpa: '',
    // Off until somebody says otherwise — approving is the deliberate act.
    approved: false,
    actionType: BANNER_ACTION.none,
    actionValue: '',
    ordering: 0,
    businessId: null,
    backgroundColor: '',
    // On, unlike the original. It seeded a new banner as `{}`, so every
    // unticked switch went out undefined and bound to false: a banner created
    // and approved without touching these showed in neither the app nor the
    // web, and nothing on the screen said why.
    showInApp: true,
    showInWeb: true,
  };
}
